import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  ClickAwayListener,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { useTranslation } from "react-i18next";
import Button from "components/CustomButtons/Button.js";
import ModalHeader from "components/ModalHeader";
import modalStyle from "assets/jss/material-dashboard-pro-react/modalStyle.js";
import {
  useFilterItemSearch,
  FILTER_SEARCH_MIN_CHARS,
} from "../../hooks/useFilterItemSearch";

const SLIM_SCROLL_CSS = `
  .dim-filter-scroll {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
  }
  .dim-filter-scroll:hover { scrollbar-color: #bbb transparent; }
  .dim-filter-scroll::-webkit-scrollbar { width: 4px; }
  .dim-filter-scroll::-webkit-scrollbar-track { background: transparent; }
  .dim-filter-scroll::-webkit-scrollbar-thumb { background: transparent; border-radius: 2px; }
  .dim-filter-scroll:hover::-webkit-scrollbar-thumb { background: #ccc; }
`;

const useDialogStyles = makeStyles((theme) => modalStyle(theme));

/**
 * Modal de seleção de filtro por dimensão.
 *
 * Props:
 *   dim           { key, label, items[], groups{key:label}, hasRefRange, refLineKey, fixedRefLine? }
 *                 fixedRefLine: { key, label } — quando definido, o seletor de linha P&L
 *                 no filtro por range vira um texto fixo (não editável pelo usuário).
 *   existingFilter { mode, items[], groupKey?, rangeMode?, rangeMin?, rangeMax?, refLine?, multiSelect? }
 *   onApply(filterObj)  – aplica seleção
 *   onClear()           – remove filtro
 *   onClose()           – fecha sem salvar
 *   open
 *   clientColor
 *   searchEndpoint  – POST { filter, q } → { items[] }; null = filtragem local apenas
 */
export default function DimFilterDialog({
  open,
  dim,
  existingFilter,
  onApply,
  onClear,
  onClose,
  clientColor,
  searchEndpoint,
  availableItems,
}) {
  const classes = useDialogStyles();
  const color = clientColor || "#4caf50";
  const { t } = useTranslation();

  // Linhas do P&L disponíveis para o filtro por referência (traduzidas)
  const PL_LINES = useMemo(
    () => [
      {
        key: "gross_margin",
        label: t("results.filter_bar.pl_lines.gross_margin"),
      },
      {
        key: "serve_margin",
        label: t("results.filter_bar.pl_lines.serve_margin"),
      },
      {
        key: "logistics_cost",
        label: t("results.filter_bar.pl_lines.logistics_cost"),
      },
      {
        key: "net_margin",
        label: t("results.filter_bar.pl_lines.net_margin"),
      },
      {
        key: "commission",
        label: t("results.filter_bar.pl_lines.commission"),
      },
      { key: "tax", label: t("results.filter_bar.pl_lines.tax") },
    ],
    [t],
  );

  const hasGroups = !!(dim?.groups && Object.keys(dim.groups).length > 0);
  const hasRefRange = !!dim?.hasRefRange;
  const fixedRefLine = dim?.fixedRefLine || null;
  const effectivePLLines = dim?.plLines?.length ? dim.plLines : PL_LINES;
  const revenueAnchors = dim?.revenueAnchors || [];

  const defaultRefLine = fixedRefLine
    ? fixedRefLine.key
    : dim?.serveMarginAnchor ||
      effectivePLLines.find((l) => l.key === dim?.refLineKey)?.key ||
      effectivePLLines.find((l) => !revenueAnchors.includes(l.key))?.key ||
      effectivePLLines[0]?.key;

  // ── estado local ──────────────────────────────────────────────────────────
  const [mode, setMode] = useState("items");
  const [multiSelect, setMultiSelect] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [rangeMode, setRangeMode] = useState("gt");
  const [rangeMin, setRangeMin] = useState("");
  const [rangeMax, setRangeMax] = useState("");
  const [refLine, setRefLine] = useState(defaultRefLine);
  const [search, setSearch] = useState("");
  const [itemsDropOpen, setItemsDropOpen] = useState(false);
  const [plLineDropOpen, setPlLineDropOpen] = useState(false);
  const searchInputRef = useRef(null);
  const itemsPanelRef = useRef(null);
  const plLinePanelRef = useRef(null);

  // Previne scroll da página ao usar wheel dentro do painel de itens
  useEffect(() => {
    const el = itemsPanelRef.current;
    if (!el || !itemsDropOpen) return;
    const handler = (e) => {
      e.preventDefault();
      el.scrollTop += e.deltaY;
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [itemsDropOpen]);

  // Previne scroll da página ao usar wheel dentro do painel de linhas do P&L
  useEffect(() => {
    const el = plLinePanelRef.current;
    if (!el || !plLineDropOpen) return;
    const handler = (e) => {
      e.preventDefault();
      el.scrollTop += e.deltaY;
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [plLineDropOpen]);

  // Auto-focus no campo de busca ao abrir o painel
  useEffect(() => {
    if (!itemsDropOpen) return;
    const timer = setTimeout(() => searchInputRef.current?.focus(), 30);
    return () => clearTimeout(timer);
  }, [itemsDropOpen]);

  // Busca com debounce
  const {
    searchResults,
    isSearching,
    search: triggerSearch,
    reset: resetSearch,
  } = useFilterItemSearch({ searchEndpoint });

  // Auto-focus no input de range
  const rangeMinRef = useRef(null);

  useEffect(() => {
    if (mode === "range") {
      const timer = setTimeout(() => rangeMinRef.current?.focus(), 40);
      return () => clearTimeout(timer);
    }
  }, [mode, rangeMode]);

  // ── sincroniza ao abrir via useEffect (evita side effects durante render) ─
  useEffect(() => {
    if (!open) return;
    const ef = existingFilter;
    const m = ef?.mode || "items";
    setMode(m);
    setMultiSelect(ef?.multiSelect ?? false);
    // Converte null de volta para o sentinel ao carregar filtro existente
    const loadedItems =
      m === "items"
        ? (ef?.items ?? []).map((it) => (it === null ? BLANK_SENTINEL : it))
        : [];
    setSelectedItems(loadedItems);
    setSelectedGroup(m === "group" ? ef?.groupKey ?? null : null);
    setRangeMode(ef?.rangeMode ?? "gt");
    setRangeMin(ef?.rangeMin ?? "");
    setRangeMax(ef?.rangeMax ?? "");
    setRefLine(ef?.refLine ?? defaultRefLine);
    setSearch("");
    resetSearch();
    setItemsDropOpen(false);
    setPlLineDropOpen(false);
  }, [open]); // eslint-disable-line

  // Sentinel para itens nulos/em branco vindos da API
  const BLANK_SENTINEL = "__blank__";

  // Rótulo de exibição para itens em branco/nulos
  const blankLabel = t("results.filter_bar.blank_item_label");

  // Retorna o rótulo de exibição de um item (substitui brancos pelo label traduzido)
  const getItemDisplayLabel = (item) => {
    if (
      item === BLANK_SENTINEL ||
      item === "" ||
      (typeof item === "string" && item.trim() === "")
    ) {
      return blankLabel;
    }
    return item;
  };

  // Resumo da seleção atual para o trigger do select compacto
  const buildSelectSummary = (items) => {
    if (items.length === 0) return t("results.filter_bar.select_items_cta");
    const displayLabels = items.map(getItemDisplayLabel);
    if (items.length === 1) return displayLabels[0];
    const joined = displayLabels.join(", ");
    if (items.length <= 5 && joined.length <= 50) return joined;
    return t("results.filter_bar.n_items", { count: items.length });
  };

  // Set de itens ainda presentes após filtros comuns (null = sem restrição)
  // IMPORTANTE: hooks devem ficar ANTES do early return para respeitar a regra de hooks
  const availableSet = useMemo(() => {
    if (!availableItems) return null;
    return new Set(
      availableItems.map((it) =>
        it === null ||
        it === undefined ||
        (typeof it === "string" && it.trim() === "")
          ? BLANK_SENTINEL
          : it,
      ),
    );
  }, [availableItems]); // eslint-disable-line

  if (!dim) return null;

  // Normaliza os itens: strings + null (mapeados para sentinel)
  const stringItems = (dim.items || []).reduce((acc, it) => {
    if (it === null || it === undefined) {
      if (!acc.includes(BLANK_SENTINEL)) acc.push(BLANK_SENTINEL);
    } else if (typeof it === "string") {
      acc.push(it === "" ? BLANK_SENTINEL : it);
    }
    return acc;
  }, []);

  // Ordena: disponíveis primeiro quando há restrição de filtro comum
  const sortedStringItems = (() => {
    if (!availableSet) return stringItems;
    const avail = [];
    const unavail = [];
    stringItems.forEach((it) => {
      if (availableSet.has(it)) avail.push(it);
      else unavail.push(it);
    });
    return [...avail, ...unavail];
  })();

  // Filtra sobre TODOS os itens, mas renderiza no máximo MAX_VISIBLE
  const MAX_VISIBLE = 300;
  const localFiltered = sortedStringItems.filter((it) =>
    getItemDisplayLabel(it).toLowerCase().includes(search.toLowerCase()),
  );
  const baseItems = searchResults !== null ? searchResults : localFiltered;
  const displayItems = baseItems.slice(0, MAX_VISIBLE);
  const hasMore = baseItems.length > MAX_VISIBLE;

  // ── seleção de itens ──────────────────────────────────────────────────────
  const activateItems = () => {
    setMode("items");
    setSelectedGroup(null);
  };

  const toggleItem = (item, e) => {
    activateItems();
    // CTRL ou SHIFT + click promove automaticamente para seleção múltipla
    const withModifier = e && (e.ctrlKey || e.metaKey || e.shiftKey);
    if (multiSelect || withModifier) {
      if (withModifier && !multiSelect) setMultiSelect(true);
      setSelectedItems((prev) =>
        prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item],
      );
    } else {
      setSelectedItems((prev) =>
        prev.length === 1 && prev[0] === item ? [] : [item],
      );
      setItemsDropOpen(false);
    }
  };

  // ── estilos de caixa ─────────────────────────────────────────────────────
  const sectionBox = (sectionMode) => ({
    border: `1px solid ${mode === sectionMode ? color : "#e8e8e8"}`,
    borderRadius: 6,
    padding: "8px 12px",
    marginBottom: 10,
    background: mode === sectionMode ? `${color}04` : "#fafafa",
    boxShadow: mode === sectionMode ? `0 0 6px #00000055` : "none",
    cursor: mode !== sectionMode ? "pointer" : "default",
    transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s",
  });

  const sectionTitle = (sectionMode) => ({
    fontSize: 10,
    color: mode === sectionMode ? color : "#aaa",
    textTransform: "uppercase",
    fontWeight: mode === sectionMode ? 600 : 400,
    marginBottom: 4,
    marginLeft: 2,
    transition: "color 0.15s",
    userSelect: "none",
  });

  // ── validação ─────────────────────────────────────────────────────────────
  const canApply =
    mode === "items"
      ? selectedItems.length > 0
      : mode === "group"
        ? !!selectedGroup
        : rangeMode === "between"
          ? rangeMin !== "" && rangeMax !== ""
          : rangeMin !== "";

  const handleApply = () => {
    if (!canApply) return;
    if (mode === "items") {
      // Mapeia o sentinel de volta para null antes de enviar para a API
      const itemsForApi = selectedItems.map((it) =>
        it === BLANK_SENTINEL ? null : it,
      );
      onApply({ mode: "items", items: itemsForApi, multiSelect });
    } else if (mode === "group") {
      onApply({
        mode: "group",
        groupKey: selectedGroup,
        items: [dim.groups[selectedGroup]],
      });
    } else {
      const lineName =
        dim.fixedRefLine?.label ||
        effectivePLLines.find((l) => l.key === refLine)?.label ||
        refLine;
      let label;
      if (rangeMode === "gt") label = `${lineName} > ${rangeMin}%`;
      else if (rangeMode === "lt") label = `${lineName} < ${rangeMin}%`;
      else label = `${lineName}: ${rangeMin}% – ${rangeMax}%`;
      onApply({
        mode: "range",
        rangeMode,
        rangeMin,
        rangeMax,
        refLine,
        items: [label],
      });
    }
  };

  const showSearchApiHint =
    searchEndpoint &&
    search.length > 0 &&
    search.length < FILTER_SEARCH_MIN_CHARS;
  const activeFilterCount =
    mode === "items"
      ? selectedItems.length
      : mode === "group"
        ? selectedGroup
          ? 1
          : 0
        : canApply
          ? 1
          : 0;
  const headerPrefix = t("results.filter_bar.dialog_header_prefix")
    .replace(/[—-]\s*$/, "")
    .trim();

  return (
    <>
      <style>{SLIM_SCROLL_CSS}</style>
      <Dialog
        classes={{ root: classes.modalRoot, paper: classes.modal }}
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        keepMounted={false}
      >
        {/* ── cabeçalho ─────────────────────────────────────────────────── */}
        <ModalHeader
          title={`${headerPrefix} ${dim.label}`}
          subtitleParts={[
            t("sidebar_cts.results"),
            <strong key="dim-label">{dim.label}</strong>,
          ]}
          pills={
            activeFilterCount > 0
              ? [
                  {
                    key: "active",
                    label: t("results.filter_bar.active_count", {
                      count: activeFilterCount,
                    }),
                  },
                ]
              : []
          }
          onClose={onClose}
          closeLabel={t("modeling_inputs_v2.drawer.close", "Fechar")}
          clientColor={clientColor}
        />

        {/* ── corpo ─────────────────────────────────────────────────────── */}
        <DialogContent
          className={classes.modalBody}
          style={{
            paddingTop: 14,
            paddingBottom: 0,
            paddingLeft: 15,
            paddingRight: 15,
          }}
        >
          {/* ── seção: por itens ──────────────────────────────────────── */}
          <div style={sectionBox("items")} onClick={activateItems}>
            {/* Cabeçalho: título à esquerda + toggle à direita */}
            <div
              style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <span style={sectionTitle("items")}>
                {t("results.filter_bar.by_items")}
              </span>

              {/* Toggle individual / múltipla */}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 11,
                  marginLeft: "auto",
                }}
              >
                <span
                  style={{
                    color: "#ccc",
                    userSelect: "none",
                    paddingBottom: 1,
                    marginRight: 4,
                  }}
                >
                  {t("results.filter_bar.selection_label")}
                </span>
                {[
                  {
                    val: false,
                    label: t("results.filter_bar.selection_individual"),
                  },
                  {
                    val: true,
                    label: t("results.filter_bar.selection_multiple"),
                  },
                ].map(({ val, label }, i) => (
                  <React.Fragment key={label}>
                    {i > 0 && (
                      <span style={{ color: "#ddd", userSelect: "none" }}>
                        ·
                      </span>
                    )}
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        if (val === false && selectedItems.length > 1)
                          setSelectedItems((prev) => [prev[0]]);
                        setMultiSelect(val);
                        activateItems();
                      }}
                      style={{
                        cursor: "pointer",
                        userSelect: "none",
                        color: multiSelect === val ? "#444" : "#bbb",
                        fontWeight: multiSelect === val ? 500 : 400,
                        borderBottom:
                          multiSelect === val
                            ? "1px solid #aaa"
                            : "1px solid transparent",
                        transition: "all 0.1s",
                      }}
                    >
                      {label}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Select compacto: trigger + painel via absolute */}
            <ClickAwayListener onClickAway={() => setItemsDropOpen(false)}>
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ position: "relative" }}
              >
                {/* Trigger */}
                <div
                  onClick={() => {
                    setItemsDropOpen((v) => !v);
                    activateItems();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    padding: "6px 8px",
                    paddingRight: 26,
                    border: `1px solid ${mode === "items" && selectedItems.length > 0 ? color + "55" : "#ddd"}`,
                    borderRadius: 4,
                    fontSize: 12,
                    fontFamily: "inherit",
                    fontWeight: 400,
                    lineHeight: 1.4,
                    color:
                      selectedItems.length > 0 && mode === "items"
                        ? "#333"
                        : "#999",
                    background: `#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23aaa'/%3E%3C/svg%3E") no-repeat right 8px center`,
                    backgroundSize: "10px 6px",
                    cursor: "pointer",
                    userSelect: "none",
                    outline: "none",
                    boxSizing: "border-box",
                    marginBottom: multiSelect ? 0 : 4,
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {buildSelectSummary(selectedItems)}
                  </span>
                </div>

                {/* Painel — position absolute, abre abaixo do trigger dentro do dialog */}
                {itemsDropOpen && (
                  <div
                    ref={itemsPanelRef}
                    className="dim-filter-scroll"
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      marginTop: 2,
                      zIndex: 10,
                      maxHeight: 420,
                      overflowY: "auto",
                      border: "1px solid #e0e0e0",
                      borderRadius: 4,
                      background: "#fff",
                      overscrollBehavior: "contain",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                    }}
                  >
                    {/* Campo de busca */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 8px",
                        borderBottom: "1px solid #f0f0f0",
                        background: "#fff",
                      }}
                    >
                      <SearchIcon
                        style={{
                          fontSize: 14,
                          color: isSearching ? color : "#ccc",
                        }}
                      />
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={t("results.filter_bar.search_placeholder")}
                        value={search}
                        onChange={(e) => {
                          const q = e.target.value;
                          setSearch(q);
                          activateItems();
                          triggerSearch(q, dim.key);
                        }}
                        style={{
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontSize: 12,
                          color: "#444",
                          width: "100%",
                        }}
                      />
                      {search && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearch("");
                            resetSearch();
                          }}
                          style={{
                            color: "#ccc",
                            cursor: "pointer",
                            fontSize: 13,
                            lineHeight: 1,
                          }}
                        >
                          ×
                        </span>
                      )}
                    </div>

                    {/* Indicador de resultados / hint de API */}
                    {search && !showSearchApiHint && (
                      <div
                        style={{
                          fontSize: 10.5,
                          color: "#bbb",
                          padding: "3px 10px 0",
                        }}
                      >
                        {t("results.filter_bar.search_results_count", {
                          count: displayItems.length,
                          query: search,
                        })}
                      </div>
                    )}
                    {showSearchApiHint && (
                      <div
                        style={{
                          fontSize: 10.5,
                          color: "#c0c0c0",
                          padding: "3px 10px 0",
                        }}
                      >
                        {t("results.filter_bar.search_api_hint", {
                          count: FILTER_SEARCH_MIN_CHARS - search.length,
                        })}
                      </div>
                    )}

                    {/* Lista de itens — sem scroll próprio; o painel flutuante rola */}
                    <div
                      className="dim-filter-scroll"
                      style={{ background: "#fff" }}
                    >
                      {displayItems.length === 0 ? (
                        <div
                          style={{
                            padding: "16px 0",
                            textAlign: "center",
                            color: "#ccc",
                            fontSize: 12,
                          }}
                        >
                          {isSearching
                            ? t("results.filter_bar.searching")
                            : t("results.filter_bar.no_items_found")}
                        </div>
                      ) : (
                        <>
                          {/* Ação "selecionar todos" visível apenas durante busca ativa */}
                          {search.trim().length > 0 && baseItems.length > 0 && (
                            <div
                              onClick={() => {
                                if (!multiSelect) setMultiSelect(true);
                                setSelectedItems((prev) => {
                                  const merged = new Set([
                                    ...prev,
                                    ...baseItems,
                                  ]);
                                  return [...merged];
                                });
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "5px 10px",
                                cursor: "pointer",
                                borderBottom: "1px solid #f0f0f0",
                                background: "#fafafa",
                                fontSize: 11.5,
                                color: color,
                                fontWeight: 500,
                                userSelect: "none",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = `${color}0d`)
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "#fafafa")
                              }
                            >
                              <span>{t("results.filter_bar.select_all")}</span>
                              <span
                                style={{
                                  fontWeight: 400,
                                  color: "#aaa",
                                  fontSize: 11,
                                }}
                              >
                                ({baseItems.length})
                              </span>
                            </div>
                          )}
                          {displayItems.map((item, i) => {
                            const isSelected = selectedItems.includes(item);
                            const isUnavail = availableSet
                              ? !availableSet.has(item)
                              : false;
                            return (
                              <div
                                key={i}
                                onClick={(e) => toggleItem(item, e)}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  padding: "3px 10px",
                                  cursor: "pointer",
                                  borderBottom:
                                    i < displayItems.length - 1
                                      ? "1px solid #f8f8f8"
                                      : "none",
                                  background: isSelected
                                    ? `${color}0a`
                                    : "#fff",
                                  userSelect: "none",
                                  opacity: isUnavail && !isSelected ? 0.5 : 1,
                                }}
                              >
                                <div
                                  style={{
                                    width: 13,
                                    height: 13,
                                    borderRadius: multiSelect ? 3 : "50%",
                                    border: `1.5px solid ${isSelected ? color : isUnavail ? "#ddd" : "#ccc"}`,
                                    background: isSelected ? color : "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    transition: "all 0.1s",
                                  }}
                                >
                                  {isSelected && (
                                    <span
                                      style={{
                                        color: "#fff",
                                        fontSize: 8,
                                        lineHeight: 1,
                                      }}
                                    >
                                      ✓
                                    </span>
                                  )}
                                </div>
                                <span
                                  style={{
                                    fontSize: 12,
                                    color: isSelected
                                      ? "#333"
                                      : isUnavail
                                        ? "#bbb"
                                        : item === BLANK_SENTINEL
                                          ? "#999"
                                          : "#555",
                                    fontWeight: isSelected ? 500 : 400,
                                    fontStyle:
                                      item === BLANK_SENTINEL || isUnavail
                                        ? "italic"
                                        : "normal",
                                  }}
                                >
                                  {getItemDisplayLabel(item)}
                                </span>
                              </div>
                            );
                          })}
                        </>
                      )}
                      {hasMore && (
                        <div
                          style={{
                            padding: "7px 10px",
                            fontSize: 11,
                            color: "#aaa",
                            textAlign: "center",
                            borderTop: "1px solid #f0f0f0",
                            background: "#fafafa",
                          }}
                        >
                          {t("results.filter_bar.showing_limited", {
                            n: MAX_VISIBLE,
                            total: baseItems.length,
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </ClickAwayListener>

            {/* Linha inferior: Todos/Nenhum/Inverter + contador (slide suave) */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                maxHeight: multiSelect ? 40 : 0,
                opacity: multiSelect ? 1 : 0,
                marginTop: multiSelect ? 6 : 0,
                marginBottom: "-4px",
                transition:
                  "max-height 0.22s ease, opacity 0.18s ease, margin-top 0.22s ease",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", gap: 10, marginLeft: 5 }}>
                {[
                  {
                    labelKey: "results.filter_bar.bulk_all",
                    action: () => setSelectedItems([...stringItems]),
                    disabled: false,
                  },
                  {
                    labelKey: "results.filter_bar.bulk_none",
                    action: () => setSelectedItems([]),
                    disabled: false,
                  },
                  {
                    labelKey: "results.filter_bar.bulk_invert",
                    action: () =>
                      setSelectedItems(
                        stringItems.filter((it) => !selectedItems.includes(it)),
                      ),
                    // TODO: substituir por modo "items_except" (negação) quando o backend suportar
                    disabled: stringItems.length > MAX_VISIBLE,
                  },
                ].map(({ labelKey, action, disabled }) => (
                  <span
                    key={labelKey}
                    onClick={() => {
                      if (disabled) return;
                      action();
                      activateItems();
                    }}
                    title={
                      disabled
                        ? t("results.filter_bar.bulk_invert_disabled_tooltip")
                        : undefined
                    }
                    style={{
                      fontSize: 11,
                      color: disabled ? "#ccc" : "#999",
                      cursor: disabled ? "default" : "pointer",
                      textDecoration: disabled ? "none" : "underline",
                      textDecorationStyle: "dotted",
                      textUnderlineOffset: 2,
                    }}
                  >
                    {t(labelKey)}
                  </span>
                ))}
              </div>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 11,
                  color: "#bbb",
                  marginRight: 5,
                }}
              >
                {t("results.filter_bar.n_selected", {
                  count: selectedItems.length,
                })}
              </span>
            </div>
          </div>

          {/* ── seção: grupo predefinido ──────────────────────────────────── */}
          {hasGroups && (
            <div
              style={{ ...sectionBox("group"), marginTop: 6 }}
              onClick={() => {
                setMode("group");
                setSelectedItems([]);
              }}
            >
              <div style={sectionTitle("group")}>
                {t("results.filter_bar.preset_group")}
              </div>
              <select
                value={selectedGroup || ""}
                onFocus={() => {
                  setMode("group");
                  setSelectedItems([]);
                }}
                onChange={(e) => {
                  const val = e.target.value || null;
                  setSelectedGroup(val);
                  setMode("group");
                  setSelectedItems([]);
                }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  paddingRight: 26,
                  border: `1px solid ${mode === "group" ? color + "55" : "#ddd"}`,
                  borderRadius: 4,
                  fontSize: 12,
                  fontFamily: "inherit",
                  fontWeight: 400,
                  lineHeight: 1.4,
                  color: selectedGroup && mode === "group" ? "#333" : "#999",
                  outline: "none",
                  background: `#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23aaa'/%3E%3C/svg%3E") no-repeat right 8px center`,
                  backgroundSize: "10px 6px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  cursor: "pointer",
                }}
              >
                <option value="" style={{ color: "#999" }}>
                  {t("results.filter_bar.select_group_placeholder")}
                </option>
                {Object.entries(dim.groups).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ── seção: filtro por P&L ─────────────────────────────────────── */}
          {hasRefRange && (
            <div
              style={{ ...sectionBox("range"), marginTop: 6 }}
              onClick={() => {
                setMode("range");
                setSelectedItems([]);
                setSelectedGroup(null);
              }}
            >
              <div style={sectionTitle("range")}>
                {t("results.filter_bar.filter_by")}
              </div>
              {fixedRefLine ? (
                <span
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "6px 8px",
                    border: `1px solid ${mode === "range" ? color + "55" : "#ddd"}`,
                    borderRadius: 4,
                    fontSize: 12,
                    color: mode === "range" ? "#333" : "#999",
                    background: "#f9f9f9",
                    marginBottom: 10,
                  }}
                >
                  {fixedRefLine.label}
                </span>
              ) : (
                <ClickAwayListener onClickAway={() => setPlLineDropOpen(false)}>
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ position: "relative", marginBottom: 10 }}
                  >
                    {/* Trigger */}
                    <div
                      onClick={() => {
                        setPlLineDropOpen((v) => !v);
                        setMode("range");
                        setSelectedItems([]);
                        setSelectedGroup(null);
                      }}
                      style={{
                        width: "100%",
                        padding: "6px 8px",
                        paddingRight: 26,
                        border: `1px solid ${mode === "range" ? color + "55" : "#ddd"}`,
                        borderRadius: 4,
                        fontSize: 12,
                        fontFamily: "inherit",
                        fontWeight: 400,
                        lineHeight: 1.4,
                        color: refLine ? "#333" : "#999",
                        background: `#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23aaa'/%3E%3C/svg%3E") no-repeat right 8px center`,
                        backgroundSize: "10px 6px",
                        cursor: "pointer",
                        userSelect: "none",
                        outline: "none",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {effectivePLLines.find((l) => l.key === refLine)
                          ?.label ||
                          refLine ||
                          t("results.filter_bar.select_pl_line_placeholder")}
                      </span>
                    </div>

                    {/* Painel de linhas */}
                    {plLineDropOpen && (
                      <div
                        ref={plLinePanelRef}
                        className="dim-filter-scroll"
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          marginTop: 2,
                          zIndex: 10,
                          maxHeight: 220,
                          overflowY: "auto",
                          border: "1px solid #e0e0e0",
                          borderRadius: 4,
                          background: "#fff",
                          overscrollBehavior: "contain",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                        }}
                      >
                        {effectivePLLines.map((line) => {
                          const isSelected = line.key === refLine;
                          const isRevenue = revenueAnchors.includes(line.key);
                          const depth = (line.level || 1) - 1;
                          return (
                            <div
                              key={line.key}
                              onClick={() => {
                                if (isRevenue) return;
                                setRefLine(line.key);
                                setMode("range");
                                setSelectedItems([]);
                                setSelectedGroup(null);
                                setPlLineDropOpen(false);
                              }}
                              style={{
                                padding: `5px 10px 5px ${10 + depth * 12}px`,
                                cursor: isRevenue ? "not-allowed" : "pointer",
                                fontSize: 12,
                                fontWeight:
                                  line.isParent || line.level === 1 ? 600 : 400,
                                color: isRevenue
                                  ? "#ccc"
                                  : isSelected
                                    ? "#fff"
                                    : "#555",
                                backgroundColor: isSelected
                                  ? color
                                  : "transparent",
                                opacity: isRevenue ? 0.5 : 1,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected && !isRevenue)
                                  e.currentTarget.style.backgroundColor =
                                    "#f5f5f5";
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected && !isRevenue)
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                              }}
                            >
                              {line.label}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </ClickAwayListener>
              )}
              {[
                {
                  val: "gt",
                  labelKey: "results.filter_bar.range_greater_than",
                  sign: ">",
                },
                {
                  val: "lt",
                  labelKey: "results.filter_bar.range_less_than",
                  sign: "<",
                },
                {
                  val: "between",
                  labelKey: "results.filter_bar.range_between",
                  sign: "↔",
                },
              ].map(({ val, labelKey, sign }) => {
                const isActive = rangeMode === val && mode === "range";
                return (
                  <div
                    key={val}
                    onClick={(e) => {
                      e.stopPropagation();
                      setRangeMode(val);
                      setMode("range");
                      setSelectedItems([]);
                      setSelectedGroup(null);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "5px 6px",
                      borderRadius: 4,
                      cursor: "pointer",
                      background: isActive ? `${color}08` : "transparent",
                      transition: "background 0.1s",
                    }}
                  >
                    <div
                      style={{
                        width: 13,
                        height: 13,
                        borderRadius: "50%",
                        border: `1.5px solid ${isActive ? color : "#ccc"}`,
                        background: isActive ? color : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.1s",
                      }}
                    >
                      {isActive && (
                        <div
                          style={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            background: "#fff",
                          }}
                        />
                      )}
                    </div>
                    <span
                      style={{
                        fontSize: 12,
                        color: isActive ? "#333" : "#888",
                        minWidth: 90,
                        userSelect: "none",
                      }}
                    >
                      {t(labelKey)}{" "}
                      <span
                        style={{
                          fontWeight: 700,
                          color: isActive ? color : "#ccc",
                        }}
                      >
                        {sign}
                      </span>
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        marginLeft: "auto",
                        visibility: isActive ? "visible" : "hidden",
                      }}
                    >
                      <input
                        ref={val === rangeMode ? rangeMinRef : null}
                        type="number"
                        value={rangeMin}
                        onChange={(e) => setRangeMin(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder={
                          val === "between"
                            ? t("results.filter_bar.range_min_placeholder")
                            : t("results.filter_bar.range_value_placeholder")
                        }
                        style={{
                          width: 64,
                          padding: "3px 8px",
                          border: "1px solid #ddd",
                          borderRadius: 4,
                          fontSize: 12,
                          outline: "none",
                          textAlign: "right",
                        }}
                      />
                      <span style={{ fontSize: 11, color: "#aaa" }}>%</span>
                      {val === "between" && (
                        <>
                          <span style={{ fontSize: 11, color: "#bbb" }}>
                            {t("results.filter_bar.range_to")}
                          </span>
                          <input
                            type="number"
                            value={rangeMax}
                            onChange={(e) => setRangeMax(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder={t(
                              "results.filter_bar.range_max_placeholder",
                            )}
                            style={{
                              width: 64,
                              padding: "3px 8px",
                              border: "1px solid #ddd",
                              borderRadius: 4,
                              fontSize: 12,
                              outline: "none",
                              textAlign: "right",
                            }}
                          />
                          <span style={{ fontSize: 11, color: "#aaa" }}>%</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>

        {/* ── rodapé ────────────────────────────────────────────────────────── */}
        <DialogActions
          className={classes.modalFooter}
          style={{ padding: "0 0 5px 0" }}
        >
          <Button
            onClick={existingFilter ? onClear : undefined}
            simple
            color="transparent"
            disabled={!existingFilter}
            style={{
              marginRight: "auto",
              marginLeft: 6,
              opacity: existingFilter ? 1 : 0.45,
              color: existingFilter ? undefined : "#888",
            }}
          >
            {t("results.filter_bar.clear_filter")}
          </Button>
          <Button onClick={onClose} simple color="transparent">
            {t("cancel")}
          </Button>
          <Button
            onClick={handleApply}
            disabled={!canApply}
            color="client"
            simple
          >
            {t("results.filter_bar.apply")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

DimFilterDialog.defaultProps = {
  existingFilter: null,
  clientColor: null,
  searchEndpoint: null,
  availableItems: null,
};

DimFilterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  dim: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.string),
    groups: PropTypes.object,
    hasRefRange: PropTypes.bool,
    refLineKey: PropTypes.string,
    fixedRefLine: PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
    }),
    plLines: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        level: PropTypes.number,
        isParent: PropTypes.bool,
      }),
    ),
    serveMarginAnchor: PropTypes.string,
    revenueAnchors: PropTypes.arrayOf(PropTypes.string),
  }),
  existingFilter: PropTypes.shape({
    mode: PropTypes.oneOf(["items", "group", "range"]),
    items: PropTypes.arrayOf(PropTypes.string),
    groupKey: PropTypes.string,
    multiSelect: PropTypes.bool,
    rangeMode: PropTypes.string,
    rangeMin: PropTypes.string,
    rangeMax: PropTypes.string,
    refLine: PropTypes.string,
  }),
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  clientColor: PropTypes.string,
  searchEndpoint: PropTypes.string,
  availableItems: PropTypes.arrayOf(PropTypes.string),
};
